/**
 * dashboard/reports/compose.tsx — Tutor compose report page (N-18).
 *
 * Lives at /dashboard/reports/compose. Tutor-only route that wraps the
 * <ReportComposer> with a class selector + preview shell. URL search
 * param `classId` (optional) pre-selects the class.
 */

import { createFileRoute, redirect, useSearch, Link } from '@tanstack/react-router';
import { z } from 'zod';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { ReportComposer } from '../../../components/tutor/ReportComposer.js';
import { classesQuery, freshSessionQuery } from '../../../lib/api/queries.js';

const searchSchema = z.object({
  classId: z.string().optional(),
});

export const Route = createFileRoute('/dashboard/reports/compose')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: ReportComposePage,
});

function ReportComposePage(): ReactNode {
  const search = useSearch({ from: '/dashboard/reports/compose' });
  const classId = search.classId ?? '';
  const navigate = Route.useNavigate();
  const classes = useQuery(classesQuery);

  if (classId) {
    return (
      <main className="page-shell" data-testid="report-compose-page">
        <PageHeader
          eyebrow="Rapor orang tua"
          title="Buat rapor progres"
          description="Kirim rapor progres siswa ke orang tua via email dan WhatsApp."
          action={
            <Link to="/dashboard/reports/history" className="button button-secondary">
              Riwayat rapor
            </Link>
          }
        />
        <ReportComposer classId={classId} />
      </main>
    );
  }

  // No ?classId= — render a class picker wired to classesQuery that
  // navigates with ?classId= instead of a static dead-end message.
  const classList = classes.data?.ok ? classes.data.data.classes : [];

  return (
    <main className="page-shell" data-testid="report-compose-page">
      <PageHeader
        eyebrow="Rapor orang tua"
        title="Buat rapor progres"
        description="Kirim rapor progres siswa ke orang tua via email dan WhatsApp."
        action={
          <Link to="/dashboard/reports/history" className="button button-secondary">
            Riwayat rapor
          </Link>
        }
      />
      <div className="report-compose-class-picker">
        {classes.isLoading ? (
          <p className="form-message">Memuat daftar kelas…</p>
        ) : classes.isError || classList.length === 0 ? (
          <p className="form-message">
            Belum ada kelas yang bisa dipilih. Buat kelas dulu dari halaman dashboard.
          </p>
        ) : (
          <label className="form-field">
            <span>Pilih kelas</span>
            <select
              data-testid="report-compose-class-select"
              defaultValue=""
              onChange={(e) => {
                const next = e.target.value;
                if (next) {
                  void navigate({ search: { classId: next } });
                }
              }}
            >
              <option value="">— pilih kelas —</option>
              {classList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
    </main>
  );
}